import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../api/api";

export const getScholarshipId = createAsyncThunk(
  "auth/getScholarshipId",
  async (payload) => {
    // Check if URL contains a "key" parameter
    const url = window.location.href;
    const parts = url.split("/");
    const lastSegment = parts.pop() || parts.pop(); // handle potential trailing slash
    let getLatestScholarshipIdApi = `${process.env.REACT_APP_API_URL}tracs_v3/scholarships/Scholarships_unprotected_api/getLatestScholarshipId?type=${payload.scholarshipType}`;

    if (lastSegment !== payload.scholarshipType) {
      getLatestScholarshipIdApi += `&key=${lastSegment}`;
    }

    const response = await api.get(getLatestScholarshipIdApi);
    return response.data;
  }
);

export const postVerifyCode = createAsyncThunk(
  "auth/postVerifyCode",
  async (payload) => {
    const response = await api.post(
      `${process.env.REACT_APP_API_URL}tracs_v3/scholarships/Scholarships_unprotected_api/verifyCode`,
      payload
    );

    return response.message;
  }
);

export const postVerifyCodeBypass = createAsyncThunk(
  "auth/postVerifyCodeBypass",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}tracs_v3/scholarships/Scholarships_unprotected_api/verifyCodeBypass`,
        payload
      );
      return response.message;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const postLogin = createAsyncThunk(
  "auth/postLogin",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}tracs_v3/scholarships/Scholarships_unprotected_api/login`,
        payload
      );
      return response.message;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState: {
    isAuthorized: localStorage.getItem("token")
      ? localStorage.getItem("token")
      : false,
    isLoggedIn: localStorage.getItem("token")
      ? localStorage.getItem("token")
      : false,
    token: localStorage.getItem("token")
      ? btoa(localStorage.getItem("token"))
      : null,
    email: null,
    status: "idle",
    error: null,
    scholarshipId: null,
    isActive: true,
    scholarshipType: null,
  },
  reducers: {
    handleInvalidToken: (state) => {
      state.isLoggedIn = false;
      state.isAuthorized = false;
      state.token = null;
      state.error = "Your session timed out. Please login again.";
    },
    handleAPIError: (state) => {
      state.isLoggedIn = false;
      state.isAuthorized = false;
      state.token = null;
      state.error =
        "Technical error: Please try again in an hour and if you still have issues contact sfu_uggla_scholars@sfu.ca for assistance";
    },
  },
  extraReducers: {
    [postVerifyCode.pending]: (state) => {
      state.status = "loading";
    },
    [postVerifyCode.fulfilled]: (state, action) => {
      state.status = "succeeded";
      state.email = action.meta.arg.email;
      state.token = action.payload;
      state.isLoggedIn = true;
    },
    [postVerifyCode.rejected]: (state) => {
      state.status = "failed";
      state.token = null;
      state.error = "Invalid verification code.";
    },
    [postVerifyCodeBypass.pending]: (state) => {
      state.status = "loading";
    },
    [postVerifyCodeBypass.fulfilled]: (state, action) => {
      state.status = "succeeded";
      state.token = action.payload;
      state.isLoggedIn = true;
    },
    [postVerifyCodeBypass.rejected]: (state, action) => {
      state.status = "failed";
      state.token = null;
      if (action.payload.httpStatus === 401) {
        state.error =
          "Invalid Student ID for this link. Please ensure your student ID is correct, if it is contact sfu_uggla_scholars@sfu.ca for assistance";
      } else {
        state.error =
          "Technical error: Please try again in an hour and if you still have issues contact sfu_uggla_scholars@sfu.ca for assistance";
      }
    },
    [postLogin.pending]: (state) => {
      state.status = "loading";
    },
    [postLogin.fulfilled]: (state, action) => {
      state.status = "succeeded";
      state.email = action.meta.arg.email;
      state.isAuthorized = true;
      state.error = null;
    },
    [postLogin.rejected]: (state, action) => {
      state.status = "failed";
      if (action.payload.httpStatus === 401) {
        // If needing to do more granular error messaging look at action.payload.body.error
        state.error =
          "Your login has failed. This may have occurred if you did not use the email that was used to complete your application to SFU, you did not enter a valid SFU ID number, or you are not eligible as you did not apply to a Simon Fraser University program as a High School Applicant. If you feel you are receiving this message in error please contact sfu_uggla_scholars@sfu.ca";
      } else if (action.payload.httpStatus === 403) {
        state.error =
          "Login Failed. This scholarship application period has closed.";
      } else {
        state.error =
          "Technical error: Please try again in an hour and if you still have issues contact sfu_uggla_scholars@sfu.ca for assistance";
      }
    },
    [getScholarshipId.pending]: (state) => {
      state.status = "loading";
    },
    [getScholarshipId.fulfilled]: (state, action) => {
      state.status = "succeeded";
      state.scholarshipId = action.payload.scholarshipId;
      state.isActive = action.payload.isActive;
      state.scholarshipType = action.meta.arg.scholarshipType;
    },
    [getScholarshipId.rejected]: () => {},
  },
});

export const { login, logout, handleInvalidToken, handleAPIError } =
  authSlice.actions;

export const selectToken = (state) => state.auth.token;
export const selectIsLoggedIn = (state) => state.auth.isLoggedIn;
export const selectIsAuthorized = (state) => state.auth.isAuthorized;
export const selectError = (state) => state.auth.error;
export const selectEmail = (state) => state.auth.email;
export const selectAuthStatus = (state) => state.auth.status;
export const selectScholarshipId = (state) => state.auth.scholarshipId;
export const selectScholarshipIsActive = (state) => state.auth.isActive;
export const selectScholarshipType = (state) => state.auth.scholarshipType;

export default authSlice.reducer;
